import React, { useContext, useMemo, useState } from "react";

import {
  Box,
  CircularProgress,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { UserStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { updateUsersSeatType } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DisplayError from "components/shared/display-error";
import { subscriptionSeatTypesMapping } from "constants/seats";
import { SubscriptionSeatUsageContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

interface ChangeUserSeatTypeSelectedUsersProps {
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  userIds: string[];
  userSeatType?: SubscriptionSeatTypes;
  isPremiumOrg: boolean;
  usersCurrentStatus: UserStatus;
  handleRefreshOrgDisplay: () => void;
  handleRefreshUserList: () => void;
  firstName?: string;
  lastName?: string;
}

const ChangeUserSeatTypeSelectedUsers = ({
  open,
  setOpen,
  userIds,
  userSeatType,
  isPremiumOrg,
  usersCurrentStatus,
  handleRefreshOrgDisplay,
  handleRefreshUserList,
  firstName,
  lastName,
}: ChangeUserSeatTypeSelectedUsersProps) => {
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [seatType, setSeatType] = useState(
    SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
  );
  const { subscriptionSeatUsage } = useContext(SubscriptionSeatUsageContext);
  const { showError } = useContext(SnackbarContext);

  const unusedFullSeats = useMemo(() => {
    if (subscriptionSeatUsage?.premiumSeatUsageSummary?.unusedSeats) {
      return subscriptionSeatUsage.premiumSeatUsageSummary.unusedSeats;
    }
    return 0;
  }, [subscriptionSeatUsage]);

  const occupiedFullSeats = useMemo(() => {
    if (subscriptionSeatUsage?.premiumSeatUsageSummary?.occupiedSeats) {
      return subscriptionSeatUsage.premiumSeatUsageSummary.occupiedSeats;
    }
    return 0;
  }, [subscriptionSeatUsage]);

  const unusedLiteSeats = useMemo(() => {
    if (subscriptionSeatUsage?.liteSeatUsageSummary?.unusedSeats) {
      return subscriptionSeatUsage.liteSeatUsageSummary.unusedSeats;
    }
    return 0;
  }, [subscriptionSeatUsage]);

  const handleSeatTypeChange = (
    event: SelectChangeEvent<SubscriptionSeatTypes>
  ) => {
    setSeatType(event.target.value as number);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    await updateUsersSeatType(
      {
        userIds,
        orgId: roleId,
        seatType,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        handleRefreshOrgDisplay();
        handleRefreshUserList();
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  let updateSeatsError = "";
  if (
    seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM &&
    unusedFullSeats < userIds.length
  ) {
    updateSeatsError =
      "Number updating must be less than or equal to the number of full seats available." +
      " Try again with fewer users updating or increase full seats first then try again.";
  }
  if (
    seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE &&
    unusedLiteSeats < userIds.length
  ) {
    updateSeatsError =
      "Number updating must be less than or equal to the number of lite seats available." +
      " Try again with fewer users updating or increase lite seats first then try again.";
  }

  if (
    (seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE ||
      seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR) &&
    occupiedFullSeats <= userIds.length &&
    !updateSeatsError
  ) {
    updateSeatsError =
      "Please ensure that there is at least one admin user as full seat in the organisation prior" +
      " to updating seat type to lite or free seat.";
  }
  return (
    <>
      <ConfirmDialog
        title="Update User Seat Type"
        disableBackgroundClick
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={open}
        setOpen={setOpen}
        onConfirm={async () => {
          await handleSubmit();
          setOpen(false);
        }}
        onCancel={() => {
          setSeatType(SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED);
        }}
        disableCloseOnConfirm
        disabled={
          !!updateSeatsError ||
          seatType === SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
        }
      >
        <DialogContent
          style={{ paddingBottom: "0" }}
          data-testid="change-users-seat-type-modal-content"
        >
          <Grid container direction="row" spacing={1}>
            <Grid item xs={12} container direction="column">
              <Grid container alignItems="center">
                <Grid item xs={12}>
                  <p data-testid="change-users-seat-type-first-last-name">
                    You are about to user{" "}
                    {`"${firstName} ${lastName}"'s seat type`}.
                  </p>
                </Grid>
              </Grid>
              {updateSeatsError && (
                <div className={clsx(classes.spaceBottom)}>
                  <DisplayError errorMessage={updateSeatsError} align="left" />
                </div>
              )}
              {isPremiumOrg && usersCurrentStatus === UserStatus.ACTIVE && (
                <Grid container alignItems="center">
                  <form
                    className={clsx(classes.spaceTopSmall)}
                    noValidate
                    autoComplete="off"
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={classes.formControl}
                    >
                      <InputLabel id="select-seat-type-label">
                        Seat Type
                      </InputLabel>
                      <Select
                        labelId="select-seat-type-label"
                        id="select-seat-type"
                        value={seatType}
                        label="Seat Type"
                        onChange={handleSeatTypeChange}
                        data-testid="update-seat-type-select"
                        inputProps={{
                          "data-testid": "seat-type-select-input",
                        }}
                      >
                        <MenuItem
                          value={
                            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
                          }
                          disabled={true}
                        >
                          {
                            subscriptionSeatTypesMapping[
                              SubscriptionSeatTypes
                                .SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
                            ]
                          }
                        </MenuItem>
                        {userSeatType !==
                          SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM && (
                          <MenuItem
                            value={
                              SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_PREMIUM
                            }
                            data-testid="update-seat-type-full-option"
                          >
                            {
                              subscriptionSeatTypesMapping[
                                SubscriptionSeatTypes
                                  .SUBSCRIPTION_SEAT_TYPE_PREMIUM
                              ]
                            }
                          </MenuItem>
                        )}
                        {userSeatType !==
                          SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR && (
                          <MenuItem
                            value={
                              SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
                            }
                            data-testid="update-seat-type-free-option"
                          >
                            {
                              subscriptionSeatTypesMapping[
                                SubscriptionSeatTypes
                                  .SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
                              ]
                            }
                          </MenuItem>
                        )}
                        {userSeatType !==
                          SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE && (
                          <MenuItem
                            value={
                              SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE
                            }
                            data-testid="update-seat-type-lite-option"
                          >
                            {
                              subscriptionSeatTypesMapping[
                                SubscriptionSeatTypes
                                  .SUBSCRIPTION_SEAT_TYPE_LITE
                              ]
                            }
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </form>
                </Grid>
              )}
            </Grid>
          </Grid>
          {isLoading && (
            <Grid
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1.5rem",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                }}
              >
                <CircularProgress
                  color="primary"
                  variant="indeterminate"
                  thickness={4}
                  size={50}
                />
              </Box>
            </Grid>
          )}
        </DialogContent>
      </ConfirmDialog>
    </>
  );
};

export default ChangeUserSeatTypeSelectedUsers;
